'use client';

import { type ReactNode, useEffect, useRef, useState } from 'react';

import type { GroupButtonViewModel } from '../types/GroupButtonViewModel';

interface Props {
  group: GroupButtonViewModel;
  /**
   * Each child button, pre-rendered into a node keyed by its `id`. The dispatch
   * layer builds this ({@link groupChildrenRecord}); the view looks each child up
   * by id for its layout. A record (not a render callback) so this client view
   * can receive the children across the server→client boundary.
   */
  renderedChildren: Record<string, ReactNode>;
  /**
   * Open-ended slot rendered right after the title (the editor fills it with
   * the group edit/pin/hide chrome). The view stays unaware of owner intent.
   */
  titleAction?: ReactNode;
  /** Open-ended slot at the far right of the header (e.g. the delete chrome). */
  headerEndAction?: ReactNode;
  /**
   * Total horizontal space (px) reserved OUTSIDE the scrollport, on both sides
   * combined, relative to the 480px bio card — so the peek-carousel sizes its
   * cards against the REAL visible width. Visitor = 36 (ProfileCardView's
   * `px-[18px]`); the editable list reserves an extra 18px gutter each side, so
   * its wrapper passes 72. Undersizing this makes the first card fill the width
   * and the next never peek.
   */
  scrollportReservePx?: number;
  className?: string;
}

// Default reserve — the visitor's ProfileCardView `px-[18px]` (18 × 2).
const DEFAULT_SCROLLPORT_RESERVE_PX = 36;

/**
 * Pure layout view for a button group — a title row + a horizontally-scrollable
 * stack of rows, each row laid out by orientation (vertical column / single
 * full-width / multi-button horizontal scroll).
 *
 * Item widths are CSS-driven (a viewport-based `calc()` minus the caller's
 * `scrollportReservePx`) so the cards render at their true width during SSR — no
 * client width measurement, no post-hydration "pop in" (the previous JS
 * `ResizeObserver` approach rendered every card at `width: 0` until it measured
 * the container after hydration). The scroll arrows stay client-only (they read
 * live scroll position). Owner edit chrome arrives through the `titleAction` /
 * `headerEndAction` slots — the view is path-agnostic.
 */
export function GroupButtonView({
  group,
  renderedChildren,
  titleAction,
  headerEndAction,
  scrollportReservePx = DEFAULT_SCROLLPORT_RESERVE_PX,
  className,
}: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Visible scrollport width, in pure CSS (no cqw / no JS measurement). The bio
  // card is `max-w-[480px]`; subtract the horizontal space reserved outside the
  // scrollport. A full-width row spans it; a horizontal card is 90% of it, so
  // the next card peeks (matches legacy `containerWidth * 0.9`).
  const scrollport = `min(100vw, 480px) - ${scrollportReservePx}px`;
  const fullRowWidth = `calc(${scrollport})`;
  const horizontalCardWidth = `calc((${scrollport}) * 0.9)`;

  const { maxButtonsInHorizontalRows, rows, titleStyle } = group;
  const needsScroll = maxButtonsInHorizontalRows > 1;
  const titleColor = titleStyle.color as string | undefined;

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  };

  // Initial arrow state: the cards are CSS-sized, so the content already
  // overflows at mount — enable the right arrow if there's more to scroll.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  }, []);

  const scrollByPage = (direction: 1 | -1) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth * 0.9, behavior: 'smooth' });
  };

  return (
    <div className={`flex w-full min-w-0 flex-col gap-2 ${className ?? ''}`}>
      <div className="flex items-center justify-between gap-2">
        <span
          className="flex-1 truncate"
          style={{ ...titleStyle, paddingRight: 4 }}
        >
          {group.title}
        </span>

        {titleAction}

        <div className="flex items-center gap-3">
          {needsScroll ? (
            <>
              <button
                type="button"
                onClick={() => scrollByPage(-1)}
                aria-label="Scroll left"
                disabled={!canScrollLeft}
                className="flex h-6 w-6 items-center justify-center rounded transition-opacity disabled:opacity-30"
                style={{ color: titleColor }}
              >
                <ChevronIcon direction="left" />
              </button>
              <button
                type="button"
                onClick={() => scrollByPage(1)}
                aria-label="Scroll right"
                disabled={!canScrollRight}
                className="flex h-6 w-6 items-center justify-center rounded transition-opacity disabled:opacity-30"
                style={{ color: titleColor }}
              >
                <ChevronIcon direction="right" />
              </button>
            </>
          ) : null}
          {headerEndAction}
        </div>
      </div>

      <div
        ref={scrollRef}
        className={
          'w-full min-w-0 ' +
          (needsScroll
            ? 'snap-x snap-mandatory overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
            : '')
        }
        style={needsScroll ? { WebkitOverflowScrolling: 'touch' } : undefined}
        onScroll={needsScroll ? handleScroll : undefined}
      >
        <div
          className={`flex flex-col gap-3 ${needsScroll ? 'w-max' : 'w-full'}`}
        >
          {rows.map(row => {
            if (row.orientation === 'vertical') {
              return (
                <div
                  key={row.id}
                  className={`flex flex-col gap-3 overflow-hidden ${needsScroll ? '' : 'w-full'}`}
                  style={needsScroll ? { width: fullRowWidth } : undefined}
                >
                  {row.buttons.map(child => (
                    <ChildSlot key={child.id}>
                      {renderedChildren[child.id]}
                    </ChildSlot>
                  ))}
                </div>
              );
            }

            if (row.buttons.length === 1) {
              return (
                <div
                  key={row.id}
                  className={`overflow-hidden ${needsScroll ? '' : 'w-full'}`}
                  style={needsScroll ? { width: fullRowWidth } : undefined}
                >
                  {renderedChildren[row.buttons[0].id]}
                </div>
              );
            }

            return (
              <div key={row.id} className="flex items-stretch gap-3">
                {row.buttons.map(child => (
                  <div
                    key={child.id}
                    className="flex-shrink-0 snap-start overflow-hidden"
                    style={{ width: horizontalCardWidth }}
                  >
                    {renderedChildren[child.id]}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ChildSlot({ children }: { children: ReactNode }) {
  return <>{children}</>;
}

function ChevronIcon({ direction }: { direction: 'left' | 'right' }) {
  return (
    <svg
      // 24px — 1:1 with legacy group rack arrows (`ChevronLeft size={rpx(24)}`).
      // Was 18px, rendering visibly smaller than legacy (QA: arrow size).
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline
        points={direction === 'left' ? '15 18 9 12 15 6' : '9 18 15 12 9 6'}
      />
    </svg>
  );
}
